import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { useLang } from "../LanguageContext";
import EditableText from "../components/EditableText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const CATEGORY_LABEL = {
  editorial: { en: "Editorial", fr: "Éditorial" },
  tips: { en: "Tips", fr: "Conseils" },
  aftercare: { en: "Aftercare", fr: "Soins post-traitement" },
  announcement: { en: "Announcement", fr: "Annonce" },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
};

export default function Journal() {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/journal`).then((r) => {
      setPosts(r.data.posts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <main data-testid="journal-page" className="pt-32 min-h-screen">
      <section className="px-6 md:px-12 lg:px-20 pb-14 md:pb-20">
        <p className="qw-overline mb-5">
          <EditableText kEn="txt_journal_overline_en" kFr="txt_journal_overline_fr" label="Journal overline" fallback={{ en: "Studio Diary", fr: "Studio Diary" }} />
        </p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground font-light leading-tight tracking-tight mb-6 max-w-3xl text-balance">
          <EditableText kEn="txt_journal_title_en" kFr="txt_journal_title_fr" label="Journal title"
            fallback={{ en: "The atelier's private journal.", fr: "Le journal intime de l'atelier." }} />
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          <EditableText kEn="txt_journal_subtitle_en" kFr="txt_journal_subtitle_fr" label="Journal subtitle"
            fallback={{
              en: "Our reflections on beauty, expert advice, favourite rituals — written by the practitioners of Queensway Studio.",
              fr: "Nos réflexions sur la beauté, nos conseils d'expertes, nos rituels préférés — écrits par les praticiennes de Queensway Studio.",
            }} />
        </p>
      </section>

      <div className="qw-divider mx-6 md:mx-12 lg:mx-20" />

      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20">
        {loading ? (
          <p className="text-muted-foreground">{lang === "fr" ? "Chargement…" : "Loading…"}</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <BookOpen size={30} strokeWidth={0.7} className="text-primary mx-auto mb-6" />
            <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-3 font-light">
              {lang === "fr" ? "Bientôt disponible." : "Coming soon."}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {lang === "fr"
                ? "Nos premiers récits arrivent. Revenez très bientôt pour lire nos rituels préférés."
                : "Our first stories are being composed. Come back very soon for our favourite rituals."}
            </p>
          </div>
        ) : (
          <ul data-testid="journal-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
            {posts.map((p, i) => (
              <li key={p.id} data-testid={`journal-card-${p.slug}`}>
                <Link to={`/journal/${p.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden bg-muted mb-6">
                    {p.cover_image ? (
                      <img
                        src={resolveImg(p.cover_image)}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <BookOpen size={26} strokeWidth={0.7} className="text-primary/60" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-baseline gap-3 mb-4">
                    <p className="qw-overline text-primary">
                      {CATEGORY_LABEL[p.category]?.[lang] || p.category}
                    </p>
                    <span className="text-foreground/30">·</span>
                    <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground">
                      {fmtDate(p.published_at, lang)}
                    </p>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-foreground group-hover:text-primary transition-colors leading-tight text-balance mb-4">
                    {lang === "fr" ? p.title_fr : p.title_en}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {lang === "fr" ? p.excerpt_fr : p.excerpt_en}
                  </p>
                  <p className="mt-5 qw-link-underline inline-flex items-center gap-1 text-[0.65rem] tracking-[0.25em] uppercase text-primary">
                    {lang === "fr" ? "Lire l'article" : "Read the article"}
                    <ArrowUpRight size={11} strokeWidth={1.2} />
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
